import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import style from "./TableSearch.module.css";
import { NavUtils } from "../../../../../../navigation";
import { isNonEmptyArray } from "../../../../../../common/utils";
import { setSorting } from "../../slice/portfolio-space-slice";
import ManageColumnsButton from "./ManageColumnsButton";

export default function TableSearchContent({
	currentPage,
	rowsPerPage,
	totalElements,
	onPageChange,
	onRowsPerPageChange,
}) {
	const { tableBody, sorting, tableColumnOrder } = useSelector(
		({ portfolioSpace }) => ({
			tableHead: portfolioSpace?.tableHead,
			tableBody: portfolioSpace?.tableBody,
			sorting: portfolioSpace?.sorting,
			tableColumnOrder: portfolioSpace?.tableColumnOrder,
		}),
		shallowEqual
	);
	const dispatch = useDispatch();
	const handleProjectRedirection = (row) => {
		const { value: cid } = row.companyId;
		const { value: pid } = row.projectId;
		if (pid && cid) {
			window.location.href = NavUtils.goToDocumentCenter(pid);
		}
	};
	const dataGridColumns = useMemo(() => {
		const visibleColumns = (tableColumnOrder || []).filter((col, index) => index === 0 || col.visibility !== false);
		const columns = visibleColumns.map((col, index) => ({
			field: col.key,
			headerName: col.name,
			disableColumnMenu: true,
			flex: 1,
			minWidth: 120,
			headerClassName: style.headerCell,
			valueGetter: (value, row) => row[col.key]?.value,
			...(index === 0 && {
				renderCell: (params) => (
					<div
						className={`${style.projectName} ${
							params.row?.projectStatus?.value === "ARCHIVED" ? style["projectName--archived"] : ""
						}`}
						role="presentation"
						onClick={() => handleProjectRedirection(params.row)}
					>
						{params.value}
					</div>
				),
			}),
		}));
		return [
			...columns,
			{
				field: "manageColumns",
				headerName: "",
				width: 40,
				sortable: false,
				disableColumnMenu: true,
				resizable: false,
				headerClassName: style.manageColumnsHeaderCell,
				renderHeader: () => <ManageColumnsButton />,
				renderCell: () => null,
			},
		];
	}, [tableColumnOrder]);
	const sortModel = sorting?.uuidOrName ? [{ field: sorting.uuidOrName, sort: sorting.order?.toLowerCase() }] : [];
	const handleSortModelChange = (newSortModel) => {
		const [next] = newSortModel;
		dispatch(setSorting(next ? { order: next.sort?.toUpperCase(), uuidOrName: next.field } : {}));
	};
	const handlePaginationModelChange = (model) => {
		if (model.pageSize !== rowsPerPage) {
			onRowsPerPageChange(model.pageSize);
			return;
		}
		if (model.page !== currentPage) {
			onPageChange(model.page);
		}
	};
	if (!isNonEmptyArray(tableColumnOrder)) {
		return null;
	}
	return (
		<DataGrid
			pagination
			className={style.dataGrid}
			columns={dataGridColumns}
			getRowId={(row) => `${row.companyId?.value}-${row.projectId?.value}`}
			initialState={{ pinnedColumns: { right: ["manageColumns"] } }}
			pageSizeOptions={[25, 50, 75, 100]}
			paginationMode="server"
			paginationModel={{ page: currentPage, pageSize: rowsPerPage }}
			rowCount={totalElements || 0}
			rows={tableBody?.contents || []}
			sortingMode="server"
			sortModel={sortModel}
			onPaginationModelChange={handlePaginationModelChange}
			onSortModelChange={handleSortModelChange}
		/>
	);
}
